import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import NotificationCenter from './NotificationCenter';
import HelpAgentWidget from './HelpAgentWidget';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  Warehouse, 
  Users, 
  ShoppingBag,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Moon,
  Sun,
  Store,
  Brain,
  ClipboardList,
  Tags,
  Scale,
  Building,
  Percent,
  ChevronDown,
  ChevronRight,
  Menu as MenuIcon,
  X,
  HardHat,
  HardDrive,
  Trash2,
  Activity,
  DollarSign
} from 'lucide-react';

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({
      'Ventas': false,
      'Finanzas': false,
      'Inventario': false,
      'Adquisiciones': false,
      'Construcción': false,
      'Administración': false
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSubmenu = (name) => {
    setExpandedMenus(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, roles: ['superadmin', 'admin', 'cajero', 'bodeguero', 'vendedor'] },
    { name: 'Panel Gerencial', path: '/super-dashboard', icon: BarChart3, roles: ['superadmin', 'admin'] },
    { name: 'POS', path: '/pos', icon: ShoppingCart, roles: ['superadmin', 'admin', 'cajero', 'vendedor'], module: 'pos' },
    
    // GROUP: Ventas
    { 
      name: 'Ventas', 
      icon: BarChart3, 
      roles: ['superadmin', 'admin', 'cajero', 'vendedor'],
      module: 'sales',
      children: [
        { name: 'Historial Ventas', path: '/sales', icon: ClipboardList, roles: ['superadmin', 'admin', 'cajero', 'vendedor'] },
        { name: 'Clientes', path: '/customers', icon: Users, roles: ['superadmin', 'admin', 'cajero', 'vendedor'] },
        { name: 'Cuentas por Cobrar', path: '/accounts-receivable', icon: ShoppingBag, roles: ['superadmin', 'admin', 'cajero', 'vendedor'] },
      ]
    },

    // GROUP: Finanzas
    { 
      name: 'Finanzas', 
      icon: DollarSign, 
      roles: ['superadmin', 'admin'],
      children: [
        { name: 'Resumen Financiero', path: '/financial-dashboard', icon: DollarSign, roles: ['superadmin', 'admin'] },
        { name: 'Gastos Operacionales', path: '/expenses', icon: ClipboardList, roles: ['superadmin', 'admin'] },
      ]
    },

    // GROUP: Inventario
    {
      name: 'Inventario',
      icon: Package,
      roles: ['superadmin', 'admin', 'bodeguero', 'vendedor'],
      module: 'inventory',
      children: [
        { name: 'Productos', path: '/products', icon: Package, roles: ['superadmin', 'admin', 'bodeguero', 'vendedor'] },
        { name: 'Gestión Inventario', path: '/inventory', icon: Warehouse, roles: ['superadmin', 'admin', 'bodeguero'] },
        { name: 'Bodegas', path: '/warehouses', icon: Building, roles: ['superadmin', 'admin'] },
        { name: 'Categorías', path: '/categories', icon: Tags, roles: ['superadmin', 'admin'] },
        { name: 'Unidades', path: '/units', icon: Scale, roles: ['superadmin', 'admin'] },
        { name: 'Promociones', path: '/promotions', icon: Percent, roles: ['superadmin', 'admin'] },
        { name: 'Mermas', path: '/wastage', icon: Trash2, roles: ['superadmin', 'admin', 'bodeguero'] },
        { name: 'Predicciones IA', path: '/ai-predictions', icon: Brain, roles: ['superadmin', 'admin', 'bodeguero'], module: 'ai_predictions' },
      ]
    },

    // GROUP: Adquisiciones
    {
      name: 'Adquisiciones',
      icon: ClipboardList,
      roles: ['superadmin', 'admin', 'bodeguero'],
      module: 'purchases',
      children: [
        { name: 'Compras', path: '/purchases', icon: FileText, roles: ['superadmin', 'admin', 'bodeguero'] },
        { name: 'Proveedores', path: '/suppliers', icon: ShoppingBag, roles: ['superadmin', 'admin', 'bodeguero'] },
      ]
    },

    // GROUP: Construcción
    {
      name: 'Construcción',
      icon: HardHat,
      roles: ['superadmin', 'admin', 'vendedor', 'bodeguero'],
      module: 'construction',
      children: [
        { name: 'Dashboard Obra', path: '/construction', icon: LayoutDashboard, roles: ['superadmin', 'admin'] },
        { name: 'Obras y Proyectos', path: '/projects', icon: Building, roles: ['superadmin', 'admin', 'vendedor'] },
        { name: 'Solicitudes de Materiales', path: '/requisitions', icon: ClipboardList, roles: ['superadmin', 'admin', 'vendedor', 'bodeguero'] },
        { name: 'Subcontratos', path: '/subcontracts', icon: FileText, roles: ['superadmin', 'admin'] },
        { name: 'Maquinaria', path: '/machinery', icon: HardDrive, roles: ['superadmin', 'admin', 'bodeguero'] },
      ]
    },
    
    // GROUP: Administración
    {
      name: 'Administración',
      icon: Settings,
      roles: ['superadmin', 'admin'],
      children: [
        { name: 'Reportes', path: '/reports', icon: ClipboardList, roles: ['superadmin', 'admin'], module: 'reports' },
        { name: 'Usuarios', path: '/users', icon: Users, roles: ['superadmin', 'admin'] },
        { name: 'Roles y Permisos', path: '/roles-permissions', icon: Brain, roles: ['superadmin', 'admin'] },
        { name: 'Configuración', path: '/settings', icon: Settings, roles: ['superadmin', 'admin'] },
        { name: 'Privacidad de Datos', path: '/gdpr-dashboard', icon: Scale, roles: ['superadmin', 'admin'] },
        { name: 'Memoria IA', path: '/settings/agent-memory', icon: Brain, roles: ['admin'] },
        { name: 'Empresas', path: '/companies', icon: Store, roles: ['superadmin'] },
        { name: 'Actividad Global', path: '/super-admin-activity', icon: Activity, roles: ['superadmin'] },
      ]
    }
  ];

  const hasRole = (roles) => {
      if (!roles) return true;
      return roles.includes(user?.role?.toLowerCase());
  };

  const hasModule = (moduleName) => {
      if (!moduleName) return true;
      if (user?.role === 'superadmin' && !user?.company_id) return true;
      if (!user?.active_modules) return false;
      return user.active_modules.includes(moduleName);
  };

  const renderNavItems = () => {
    return navigation.map((item) => {
       if (!hasRole(item.roles) || !hasModule(item.module)) return null;

       if (item.children) {
           // It's a group
           // Filter children based on role and module
           const visibleChildren = item.children.filter(child => hasRole(child.roles) && hasModule(child.module));
           if (visibleChildren.length === 0) return null;

           const isExpanded = expandedMenus[item.name];
           const Icon = item.icon;

           return (
               <div key={item.name} className="mb-1">
                   <button 
                     onClick={() => toggleSubmenu(item.name)}
                     className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/50 rounded-lg transition-colors"
                   >
                       <div className="flex items-center space-x-3">
                           <Icon className="h-5 w-5" />
                           <span>{item.name}</span>
                       </div>
                       {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                   </button>
                   
                   {isExpanded && (
                       <div className="mt-1 ml-4 space-y-1 border-l-2 border-border pl-2">
                           {visibleChildren.map(child => {
                               const isActive = location.pathname === child.path;
                               const ChildIcon = child.icon;
                               return (
                                   <Link
                                       key={child.path}
                                       to={child.path}
                                       onClick={() => setMobileMenuOpen(false)} // Close mobile menu on click
                                       className={`flex items-center space-x-3 px-4 py-2 text-sm rounded-lg transition-colors ${
                                           isActive
                                               ? 'bg-indigo-600/10 text-indigo-600 font-semibold'
                                               : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                       }`}
                                   >
                                       {/* <ChildIcon className="h-4 w-4" />  Optional: Hide icons for children to save space? Keeping for now logic */}
                                       <span>{child.name}</span>
                                   </Link>
                               );
                           })}
                       </div>
                   )}
               </div>
           );
       } else {
           // Single Item
           const isActive = location.pathname === item.path;
           const Icon = item.icon;
           return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 mb-1 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-indigo-600 text-white'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
           );
       }
    });
  };

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Sidebar (Desktop) */}
      <aside className="hidden md:flex md:flex-col w-64 border-r border-border bg-muted/30 h-full" data-testid="sidebar">
        <div className="p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-600 rounded-lg">
              <Store className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">GKService</h1>
              {user?.role && (
                 <p className="text-xs text-muted-foreground font-mono font-bold mb-0.5">{user.role.toUpperCase()}</p>
              )}
              <p className="text-[10px] text-muted-foreground truncate max-w-[160px]">
                  {user?.email}
              </p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
          {renderNavItems()}
        </nav>
        
        <div className="p-4 border-t border-border space-y-2 flex-shrink-0">
          <NotificationCenter />
          <Button
            variant="ghost"
            onClick={toggleTheme}
            className="w-full justify-start"
          >
            {theme === 'light' ? <Moon className="h-5 w-5 mr-3" /> : <Sun className="h-5 w-5 mr-3" />}
            {theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-destructive hover:text-destructive"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Menu Drawer Overlay */}
      {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm md:hidden" onClick={() => setMobileMenuOpen(false)}>
              <div 
                className="fixed inset-y-0 left-0 w-3/4 max-w-xs bg-background border-r border-border shadow-2xl p-4 overflow-y-auto flex flex-col"
                onClick={(e) => e.stopPropagation()} // Prevent close on click inside
              >
                  <div className="flex justify-between items-center mb-6">
                      <div className="flex items-center space-x-2">
                        <div className="p-2 bg-indigo-600 rounded-lg">
                            <Store className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-bold">GKService</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                          <X className="h-6 w-6" />
                      </Button>
                  </div>

                  <nav className="flex-1 space-y-1">
                      {renderNavItems()}
                  </nav>

                   <div className="pt-4 mt-4 border-t border-border space-y-2">
                      <Button variant="ghost" onClick={toggleTheme} className="w-full justify-start">
                        {theme === 'light' ? <Moon className="h-5 w-5 mr-3" /> : <Sun className="h-5 w-5 mr-3" />}
                        Tema
                      </Button>
                      <Button variant="ghost" onClick={handleLogout} className="w-full justify-start text-destructive">
                        <LogOut className="h-5 w-5 mr-3" />
                        Salir
                      </Button>
                   </div>
              </div>
          </div>
      )}


      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-40 pb-safe" data-testid="mobile-nav">
        <div className="flex justify-around p-2">
           <Link to="/" className="flex flex-col items-center p-2 text-muted-foreground hover:text-indigo-600">
             <LayoutDashboard className="h-6 w-6" />
             <span className="text-[10px] mt-1">Inicio</span>
           </Link>
           {hasModule('pos') && (
             <Link to="/pos" className="flex flex-col items-center p-2 text-muted-foreground hover:text-indigo-600">
               <ShoppingCart className="h-6 w-6" />
               <span className="text-[10px] mt-1">POS</span>
             </Link>
           )}
           {hasModule('inventory') && (
             <Link to="/products" className="flex flex-col items-center p-2 text-muted-foreground hover:text-indigo-600">
               <Package className="h-6 w-6" />
               <span className="text-[10px] mt-1">Prod.</span>
             </Link>
           )}
           <button 
             onClick={() => setMobileMenuOpen(true)}
             className={`flex flex-col items-center p-2 ${mobileMenuOpen ? 'text-indigo-600' : 'text-muted-foreground'}`}
           >
             <MenuIcon className="h-6 w-6" />
             <span className="text-[10px] mt-1">Menú</span>
           </button>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 h-full overflow-y-auto pb-20 md:pb-0">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
      <HelpAgentWidget />
    </div>
  );
}